import { useEffect, useRef } from 'react';

const readingFeatures = [
  {
    icon: 'ri-heart-line',
    title: '細膩的人物關係',
    description: '從角色之間的靠近、誤解、選擇與改變，呈現情感關係的多種面貌。',
  },
  {
    icon: 'ri-book-open-line',
    title: '具有推進感的故事',
    description: '透過情節、人物目標與關係轉折，帶領讀者持續進入故事世界。',
  },
  {
    icon: 'ri-user-line',
    title: '愛情之外的成長',
    description: '除了相遇與相愛，也關注角色如何面對傷口、責任、失去與自我理解。',
  },
];

export default function BrandValuesSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      el.querySelectorAll('[data-animate]').forEach((child) => {
        (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const children = entry.target.querySelectorAll('[data-animate]');
            children.forEach((child, i) => {
              setTimeout(() => {
                (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
              }, 100 + i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="min-h-screen-dynamic flex items-center bg-background-100">
      <div ref={ref} className="section-container w-full py-16 md:py-0">
        <div className="text-center mb-10 max-w-[800px] mx-auto">
          <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-accent-50 text-accent-700 rounded-full mb-3">
            品牌定位
          </span>
          <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
            未境原創的品牌定位
          </h2>
          <p data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-sm md:text-base text-foreground-600 leading-relaxed max-w-[720px] mx-auto mb-3">
            未境原創以原創故事為核心，透過愛情、情感關係、人物成長及類型敘事，建立具有閱讀連貫性與沉浸感的出版內容。
          </p>
          <p data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-sm md:text-base text-foreground-600 leading-relaxed max-w-[720px] mx-auto">
            品牌內容涵蓋心動相遇、日常陪伴、關係選擇與自我成長等主題，讓讀者從角色的情感歷程中，看見愛與人生的不同可能。
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-[1000px] mx-auto">
          {readingFeatures.map((feature) => (
            <div
              key={feature.title}
              data-animate
              className="opacity-0 translate-y-6 transition-all duration-700 p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-200 transition-colors"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 mb-4">
                <i className={`${feature.icon} text-accent-500 text-lg`}></i>
              </div>
              <h3 className="text-base font-label font-medium text-foreground-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed text-left">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}